<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';
  import { selectedCountry } from '../countryStore';
  import CountrySelector from '../components/CountrySelector.svelte';

  let places = [];
  let currentName = '';
  let currentNameEn = '';
  let currentNameZh = '';
  let currentNameJa = '';
  let currentShortTag = '';
  let currentCountry = $selectedCountry || 'HK';
  let currentAddress = '';
  let currentLat = '';
  let currentLng = '';
  let isEditing = false;
  let currentId = null;

  // React to country changes from navbar or page selector
  $: if ($selectedCountry) {
    if (!isEditing) {
      currentCountry = $selectedCountry;
    }
    fetchPlaces();
  }

  // Search & Pagination state for Existing Places
  let placeFilterText = '';
  let placePage = 1;
  const placePageSize = 50;

  $: filteredPlaces = places.filter(p => {
    const placeCountry = (p.countryCode || 'HK').toUpperCase();
    const targetCountry = ($selectedCountry || 'HK').toUpperCase();
    if (placeCountry !== targetCountry) return false;

    if (!placeFilterText.trim()) return true;
    const term = placeFilterText.toLowerCase().trim();
    const nameMatch = p.name && p.name.toLowerCase().includes(term);
    const enMatch = p.nameEn && p.nameEn.toLowerCase().includes(term);
    const zhMatch = p.nameZh && p.nameZh.toLowerCase().includes(term);
    const jaMatch = p.nameJa && p.nameJa.toLowerCase().includes(term);
    const tagMatch = p.shortTag && p.shortTag.toLowerCase().includes(term);
    const addrMatch = p.address && p.address.toLowerCase().includes(term);
    return nameMatch || enMatch || zhMatch || jaMatch || tagMatch || addrMatch;
  });

  $: if (placeFilterText !== undefined || $selectedCountry !== undefined) {
    placePage = 1;
  }
  $: placeTotalPages = Math.ceil(filteredPlaces.length / placePageSize) || 1;
  $: paginatedPlaces = filteredPlaces.slice((placePage - 1) * placePageSize, placePage * placePageSize);

  // Search & Loading states
  let isSearching = false;
  let isSearchingCSDI = false;
  let isLoading = true;
  let error = '';
  let successMessage = '';

  // CSDI Modal Results
  let csdiResults = [];
  let showCsdiModal = false;

  // Bulk Upload state
  let selectedFile = null;
  let isUploading = false;

  onMount(fetchPlaces);

  async function fetchPlaces() {
    isLoading = true;
    try {
      const code = $selectedCountry || 'HK';
      const res = await authFetch(getApiUrl(`/admin/places?countryCode=${encodeURIComponent(code)}`));
      if (!res.ok) throw new Error('Could not load places.');
      places = await res.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  // Auto-generate short tag on typing English or Chinese name if not manually modified
  function handleNameBlur() {
    if (!currentShortTag.trim()) {
      autoGenerateShortTag();
    }
    if (!currentName.trim()) {
      currentName = currentNameEn.trim() || currentNameZh.trim();
    }
  }

  function autoGenerateShortTag() {
    const en = currentNameEn.trim();
    const zh = currentNameZh.trim();
    if (en) {
      // CamelCase
      const words = en.split(/\s+/);
      const tag = words.map(w => w.replace(/[^a-zA-Z0-9]/g, '')).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
      if (tag) {
        currentShortTag = tag;
        return;
      }
    }
    if (zh) {
      currentShortTag = zh.replace(/[\s\-_/,().'\"#@!%&*\[\]]/g, '');
    }
  }

  // 1. Google Places Address Lookup
  async function lookupAddress() {
    const query = currentAddress.trim() || currentNameEn.trim() || currentNameZh.trim() || currentName.trim();
    if (!query) return;
    isSearching = true;
    error = '';

    try {
      const response = await authFetch(getApiUrl(`/admin/geocode?address=${encodeURIComponent(query)}`));
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Lookup failed');

      currentAddress = data.address; // Formatted address from Google
      currentLat = data.lat;
      currentLng = data.lng;
    } catch (e) {
      error = e.message;
    } finally {
      isSearching = false;
    }
  }

  // 2. Hong Kong CSDI Official Search
  async function lookupCSDI() {
    const query = currentNameEn.trim() || currentNameZh.trim() || currentName.trim() || currentAddress.trim();
    if (!query) return;
    isSearchingCSDI = true;
    error = '';
    csdiResults = [];

    try {
      const response = await authFetch(getApiUrl(`/admin/places/csdi-search?q=${encodeURIComponent(query)}`));
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'CSDI query failed');

      if (!Array.isArray(data) || data.length === 0) {
        error = $t('admin.place.csdi_none');
        return;
      }

      if (data.length === 1) {
        applyCsdiResult(data[0]);
      } else {
        csdiResults = data;
        showCsdiModal = true;
      }
    } catch (e) {
      error = e.message;
    } finally {
      isSearchingCSDI = false;
    }
  }

  function applyCsdiResult(result) {
    if (result.nameEn) currentNameEn = result.nameEn;
    if (result.nameZh) currentNameZh = result.nameZh;
    currentName = result.name || result.nameEn || result.nameZh;
    if (result.address) currentAddress = result.address;
    if (result.lat) currentLat = result.lat;
    if (result.lng) currentLng = result.lng;
    if (result.shortTag) currentShortTag = result.shortTag;
    currentCountry = result.countryCode || 'HK';

    successMessage = $t('admin.place.csdi_found').replace('{name}', currentName);
    showCsdiModal = false;
    csdiResults = [];
  }

  async function handleSubmit() {
    error = '';
    successMessage = '';

    const canonical = currentName.trim() || currentNameEn.trim() || currentNameZh.trim();
    if (!canonical) {
      error = 'Place name is required.';
      return;
    }

    const payload = {
      name: canonical,
      nameEn: currentNameEn.trim() || canonical,
      nameZh: currentNameZh.trim(),
      nameJa: currentNameJa.trim(),
      shortTag: currentShortTag.trim(),
      countryCode: currentCountry.trim().toUpperCase() || 'HK',
      address: currentAddress.trim(),
      translations: {
        en: currentNameEn.trim() || canonical,
      },
    };
    if (currentNameZh.trim()) {
      payload.translations.zh = currentNameZh.trim();
    }
    if (currentNameJa.trim()) {
      payload.translations.ja = currentNameJa.trim();
    }

    if (currentLat !== '' && currentLng !== '') {
      payload.coordinates = {
        type: 'Point',
        coordinates: [parseFloat(currentLng), parseFloat(currentLat)],
      };
    }

    const url = isEditing ? getApiUrl(`/admin/places/${currentId}`) : getApiUrl('/admin/places');
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save place.');
      }

      successMessage = isEditing ? $t('admin.place.success_update') : $t('admin.place.success_add');
      cancelEdit();
      await fetchPlaces();
    } catch (e) {
      error = e.message;
    }
  }

  function startEdit(place) {
    isEditing = true;
    currentId = place._id || place.id;
    currentName = place.name || '';
    currentNameEn = place.nameEn || place.name || '';
    currentNameZh = place.nameZh || (place.translations ? place.translations.zh : '') || '';
    currentNameJa = place.nameJa || (place.translations ? place.translations.ja : '') || '';
    currentShortTag = place.shortTag || '';
    currentCountry = place.countryCode || 'HK';
    currentAddress = place.address || '';
    if (place.coordinates && Array.isArray(place.coordinates.coordinates) && place.coordinates.coordinates.length === 2) {
      currentLng = place.coordinates.coordinates[0];
      currentLat = place.coordinates.coordinates[1];
    } else {
      currentLat = '';
      currentLng = '';
    }
    error = '';
    successMessage = '';
  }

  function cancelEdit() {
    isEditing = false;
    currentId = null;
    currentName = '';
    currentNameEn = '';
    currentNameZh = '';
    currentNameJa = '';
    currentShortTag = '';
    currentCountry = $selectedCountry || 'HK';
    currentAddress = '';
    currentLat = '';
    currentLng = '';
    error = '';
  }

  async function handleDelete(id) {
    if (!confirm($t('admin.place.delete_confirm'))) return;
    try {
      const res = await authFetch(getApiUrl(`/admin/places/${id}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete place.');
      successMessage = $t('admin.place.success_delete');
      fetchPlaces();
    } catch (e) {
      error = e.message;
    }
  }

  // --- BULK CSV IMPORT ---
  function handleFileSelect(e) {
    selectedFile = e.target.files[0];
  }

  async function handleBulkUpload() {
    if (!selectedFile) return;
    isUploading = true;
    error = '';
    successMessage = '';

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await authFetch(getApiUrl('/admin/places/upload'), {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload CSV.');

      successMessage = $t('admin.place.upload_success')
        .replace('{processed}', data.processed || 0)
        .replace('{inserted}', data.inserted || 0)
        .replace('{modified}', data.modified || 0);

      selectedFile = null;
      const fileInput = document.getElementById('places-csv-input');
      if (fileInput) fileInput.value = '';

      await fetchPlaces();
    } catch (e) {
      error = e.message;
    } finally {
      isUploading = false;
    }
  }

  function downloadTemplate() {
    const csvContent = "name,nameZh,nameJa,countryCode,shortTag,address,lat,lng\n" +
      "Times Square,時代廣場,タイムズスクエア,HK,TimesSquare,1 Matheson St Causeway Bay,22.2783,114.1822\n" +
      "Harbour City,海港城,ハーバーシティ,HK,HarbourCity,3-27 Canton Rd Tsim Sha Tsui,22.2988,114.1685\n" +
      "Langham Place,朗豪坊,ランガムプレイス,HK,LanghamPlace,8 Argyle St Mong Kok,22.3188,114.1688\n";

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'places_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
</script>

<div class="maintenance-container">
  <h2>{$t('admin.place.title')}</h2>

  {#if error}
    <div class="alert error">{error}</div>
  {/if}
  {#if successMessage}
    <div class="alert success">{successMessage}</div>
  {/if}

  <div class="grid-layout">
    <div class="action-column">
      <!-- 1. Single Place Entry Form -->
      <div class="card form-section">
        <h3>{isEditing ? $t('admin.place.edit') : $t('admin.place.add')}</h3>
        <form on:submit|preventDefault={handleSubmit}>
          <div class="form-group">
            <label for="place-name-en">{$t('admin.place.name_en')} *</label>
            <div class="input-with-action">
              <input
                type="text"
                id="place-name-en"
                bind:value={currentNameEn}
                on:blur={handleNameBlur}
                placeholder="e.g. Times Square"
                required
              />
              <button
                type="button"
                class="csdi-btn"
                on:click={lookupCSDI}
                disabled={isSearchingCSDI || (!currentNameEn && !currentNameZh)}
                title="Search Hong Kong CSDI Government Spatial Database"
              >
                {isSearchingCSDI ? '...' : '🏛️ ' + $t('admin.place.csdi_btn')}
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="place-name-zh">{$t('admin.place.name_zh')}</label>
            <input
              type="text"
              id="place-name-zh"
              bind:value={currentNameZh}
              on:blur={handleNameBlur}
              placeholder="e.g. 時代廣場"
            />
          </div>

          <div class="form-group">
            <label for="place-name-ja">{$t('admin.place.name_ja')}</label>
            <input
              type="text"
              id="place-name-ja"
              bind:value={currentNameJa}
              placeholder="e.g. タイムズスクエア"
            />
          </div>

          <div class="form-group">
            <label for="short-tag">{$t('admin.place.short_tag')}</label>
            <div class="tag-input-row">
              <span class="tag-prefix">#</span>
              <input
                type="text"
                id="short-tag"
                bind:value={currentShortTag}
                placeholder="TimesSquare"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="country-code">{$t('admin.place.country')} *</label>
            <input type="text" id="country-code" bind:value={currentCountry} placeholder="HK" required />
          </div>

          <div class="form-group">
            <label for="place-address">{$t('admin.place.address')}</label>
            <div class="address-lookup-row">
              <input
                type="text"
                id="place-address"
                bind:value={currentAddress}
                placeholder="e.g. 1 Matheson St, Causeway Bay"
              />
              <button
                type="button"
                class="lookup-btn"
                on:click={lookupAddress}
                disabled={isSearching || (!currentAddress && !currentNameEn && !currentNameZh)}
                title="Google Places Geocoding"
              >
                {isSearching ? '...' : '📍 ' + $t('admin.place.lookup_btn')}
              </button>
            </div>
          </div>

          <div class="form-group coord-row">
            <div>
              <label for="place-lat">{$t('admin.place.lat')}</label>
              <input type="number" step="any" id="place-lat" bind:value={currentLat} placeholder="22.2783" />
            </div>
            <div>
              <label for="place-lng">{$t('admin.place.lng')}</label>
              <input type="number" step="any" id="place-lng" bind:value={currentLng} placeholder="114.1822" />
            </div>
          </div>

          <div class="button-row">
            <button type="submit" class="primary-btn">{isEditing ? $t('admin.place.save') : $t('admin.place.add_btn')}</button>
            {#if isEditing}
              <button type="button" class="secondary-btn" on:click={cancelEdit}>{$t('admin.place.cancel')}</button>
            {/if}
          </div>
        </form>
      </div>

      <!-- 2. Bulk CSV Upload Section -->
      <div class="card upload-section">
        <h3>{$t('admin.place.bulk_upload')}</h3>
        <p class="hint">{$t('admin.place.bulk_upload_hint')}</p>

        <div class="upload-controls">
          <input
            type="file"
            id="places-csv-input"
            accept=".csv"
            on:change={handleFileSelect}
          />
          <div class="upload-actions">
            <button
              type="button"
              class="primary-btn"
              on:click={handleBulkUpload}
              disabled={isUploading || !selectedFile}
            >
              {isUploading ? $t('admin.place.uploading') : $t('admin.place.upload_btn')}
            </button>
            <button
              type="button"
              class="template-btn"
              on:click={downloadTemplate}
            >
              📄 {$t('admin.place.download_template')}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Places List Section -->
    <div class="card list-section">
      <div class="list-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; flex-wrap: wrap; gap: 0.5rem;">
        <h3 style="margin: 0;">{$t('admin.place.existing')} ({filteredPlaces.length})</h3>
        <div style="display: flex; align-items: center; gap: 0.5rem;">
          <CountrySelector />
        </div>
      </div>
      <div class="search-filter-box" style="margin-bottom: 1.25rem;">
        <input 
          type="text" 
          placeholder={$t('admin.place.search_placeholder')} 
          bind:value={placeFilterText} 
          style="width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;" 
        />
      </div>
      {#if isLoading}
        <p class="loading">{$t('common.loading')}</p>
      {:else if filteredPlaces.length === 0}
        <p class="empty-state">{$t('admin.place.none')}</p>
      {:else}
        <div class="places-list">
          {#each paginatedPlaces as p (p._id)}
            <div class="place-row">
              <div class="place-info">
                <div class="place-header">
                  <span class="place-title">{p.nameEn || p.name}</span>
                  {#if p.nameZh}
                    <span class="place-zh">({p.nameZh})</span>
                  {/if}
                  {#if p.nameJa}
                    <span class="place-ja">({p.nameJa})</span>
                  {/if}
                  <span class="badge country">{p.countryCode}</span>
                  {#if p.shortTag}
                    <span class="badge tag">#{p.shortTag}</span>
                  {/if}
                </div>
                {#if p.address}
                  <div class="place-addr">📍 {p.address}</div>
                {/if}
                <div class="place-coords">
                  {#if p.coordinates?.coordinates?.length === 2}
                    <span class="coord-badge">🗺️ [{p.coordinates.coordinates[1].toFixed(4)}, {p.coordinates.coordinates[0].toFixed(4)}]</span>
                  {:else}
                    <span class="no-coord-badge">{$t('admin.place.no_coords')}</span>
                  {/if}
                </div>
              </div>
              <div class="action-buttons">
                <button class="btn-edit" on:click={() => startEdit(p)}>{$t('common.edit')}</button>
                <button class="btn-del" on:click={() => handleDelete(p._id)}>{$t('common.delete')}</button>
              </div>
            </div>
          {/each}
        </div>

        {#if placeTotalPages > 1}
          <div class="pagination-row">
            <button class="secondary-btn" disabled={placePage === 1} on:click={() => placePage--}>
              &larr; {$t('common.prev')}
            </button>
            <span class="pagination-info">
              {$t('admin.place.showing_range', {
                start: (placePage - 1) * placePageSize + 1,
                end: Math.min(placePage * placePageSize, filteredPlaces.length),
                total: filteredPlaces.length
              })} &bull; {$t('common.page_of', { page: placePage, total: placeTotalPages })}
            </span>
            <button class="secondary-btn" disabled={placePage >= placeTotalPages} on:click={() => placePage++}>
              {$t('common.next')} &rarr;
            </button>
          </div>
        {/if}
      {/if}
    </div>
  </div>
</div>

<!-- CSDI Selection Modal (when multiple location matches returned) -->
{#if showCsdiModal}
  <div class="modal-backdrop" on:click={() => showCsdiModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h4>Select CSDI Official Location Match ({csdiResults.length})</h4>
        <button class="close-btn" on:click={() => showCsdiModal = false}>&times;</button>
      </div>
      <div class="csdi-list">
        {#each csdiResults as res}
          <div class="csdi-item" on:click={() => applyCsdiResult(res)}>
            <div class="csdi-names">
              <strong>{res.nameEn}</strong>
              <span class="csdi-zh">{res.nameZh}</span>
              {#if res.district}
                <span class="district-badge">{res.district}</span>
              {/if}
            </div>
            {#if res.address}
              <div class="csdi-addr">{res.address}</div>
            {/if}
            <div class="csdi-meta">
              <span>GPS: [{res.lat}, {res.lng}]</span>
              {#if res.shortTag}<span>#{res.shortTag}</span>{/if}
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>
{/if}

<style>
  .maintenance-container { max-width: 1250px; margin: 2rem auto; padding: 0 1rem; }
  .grid-layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
  @media(min-width: 900px) { .grid-layout { grid-template-columns: 460px 1fr; } }

  .action-column { display: flex; flex-direction: column; gap: 1.5rem; }
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
  h2 { margin-bottom: 1.5rem; color: #333; }
  h3 { margin-top: 0; color: #0056b3; border-bottom: 1px solid #eee; padding-bottom: 0.5rem; margin-bottom: 1rem; }

  .alert { padding: 0.75rem 1rem; border-radius: 4px; margin-bottom: 1rem; font-size: 0.9rem; }
  .alert.error { background: #ffebee; color: #c62828; border: 1px solid #ffcdd2; }
  .alert.success { background: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }

  .form-group { margin-bottom: 1rem; }
  label { display: block; margin-bottom: 0.4rem; font-weight: 600; color: #444; font-size: 0.85rem; }
  input[type="text"], input[type="number"] { width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; font-size: 0.9rem; }

  .input-with-action { display: flex; gap: 0.5rem; }
  .input-with-action input { flex: 1; }

  .csdi-btn {
    background: #1b5e20;
    color: white;
    border: none;
    padding: 0 0.8rem;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 0.85rem;
    font-weight: 600;
    transition: background 0.2s;
  }
  .csdi-btn:hover { background: #2e7d32; }
  .csdi-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .tag-input-row { display: flex; align-items: center; }
  .tag-prefix { padding: 0.6rem 0.8rem; background: #eee; border: 1px solid #ccc; border-right: none; border-radius: 4px 0 0 4px; color: #666; font-weight: bold; }
  .tag-input-row input { border-radius: 0 4px 4px 0; }

  .address-lookup-row { display: flex; gap: 0.5rem; }
  .address-lookup-row input { flex: 1; }
  .lookup-btn { background: #455a64; color: white; border: none; padding: 0 0.8rem; border-radius: 4px; cursor: pointer; white-space: nowrap; font-size: 0.85rem; }
  .lookup-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .coord-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }

  .button-row { display: flex; gap: 0.5rem; margin-top: 1.25rem; }
  .primary-btn { padding: 0.6rem 1.2rem; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 600; }
  .primary-btn:hover { background: #0056b3; }
  .primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .secondary-btn { padding: 0.6rem 1rem; background: #e0e0e0; color: #333; border: none; border-radius: 4px; cursor: pointer; }
  .secondary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .pagination-row { display: flex; justify-content: space-between; align-items: center; margin-top: 1rem; }
  .pagination-info { font-size: 0.85rem; color: #555; }

  /* Bulk Upload Section */
  .upload-section .hint { font-size: 0.8rem; color: #666; margin-bottom: 0.8rem; line-height: 1.4; }
  .upload-controls { display: flex; flex-direction: column; gap: 0.75rem; }
  .upload-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .template-btn { padding: 0.6rem 1rem; background: #f0f4f8; color: #0056b3; border: 1px solid #cce0f5; border-radius: 4px; cursor: pointer; font-size: 0.85rem; }
  .template-btn:hover { background: #e2eefb; }

  /* List Section */
  .places-list { display: flex; flex-direction: column; gap: 0.75rem; max-height: 650px; overflow-y: auto; }
  .place-row { display: flex; justify-content: space-between; align-items: center; padding: 0.85rem; border: 1px solid #e0e0e0; border-radius: 6px; background: #fafafa; }
  .place-info { display: flex; flex-direction: column; gap: 0.25rem; }
  .place-header { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .place-title { font-weight: 600; font-size: 1rem; color: #222; }
  .place-zh { font-size: 0.9rem; color: #555; }
  .place-ja { font-size: 0.85rem; color: #666; font-style: italic; }
  .badge.country { background: #e3f2fd; color: #1565c0; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: bold; }
  .badge.tag { background: #ede7f6; color: #512da8; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; }
  .place-addr { font-size: 0.85rem; color: #555; }
  .place-coords { font-size: 0.8rem; margin-top: 2px; }
  .coord-badge { color: #2e7d32; background: #e8f5e9; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.8rem; }
  .no-coord-badge { color: #888; font-style: italic; }

  .action-buttons { display: flex; gap: 0.5rem; }
  .btn-edit { background: #e3f2fd; color: #0d47a1; border: 1px solid #bbdefb; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.85rem; }
  .btn-del { background: #ffebee; color: #c62828; border: 1px solid #ffcdd2; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.85rem; }
  .loading, .empty-state { color: #888; text-align: center; padding: 2rem 0; }

  /* Modal */
  .modal-backdrop { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
  .modal-content { background: white; border-radius: 8px; max-width: 600px; width: 90%; max-height: 80vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
  .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-bottom: 1px solid #eee; }
  .modal-header h4 { margin: 0; color: #1b5e20; }
  .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #888; }
  .csdi-list { overflow-y: auto; padding: 0.5rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .csdi-item { padding: 0.75rem; border: 1px solid #e0e0e0; border-radius: 6px; cursor: pointer; transition: background 0.15s, border-color 0.15s; }
  .csdi-item:hover { background: #f1f8e9; border-color: #81c784; }
  .csdi-names { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .csdi-zh { color: #444; font-size: 0.9rem; }
  .district-badge { background: #e0f2f1; color: #00695c; padding: 1px 6px; border-radius: 4px; font-size: 0.75rem; }
  .csdi-addr { font-size: 0.85rem; color: #555; margin-top: 2px; }
  .csdi-meta { display: flex; gap: 1rem; font-size: 0.8rem; color: #777; margin-top: 4px; }
</style>
